export enum PerformanceTrackerStatus {
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
  Canceled = 'canceled',
}

interface PerformanceTrackerInternalState {
  status: PerformanceTrackerStatus;
  error?: Error;
}

export class PerformanceTracker {
  private state: PerformanceTrackerInternalState;

  private key: string;

  private tags: string[];

  private notifyCallback: (error: unknown, info?: unknown) => void;

  constructor(
    key: string,
    {
      tags = [],
    }: {
      tags?: string[];
    },
    notifyCallback: (error: unknown, info?: unknown) => void
  ) {
    this.key = key;
    this.tags = tags;
    this.notifyCallback = notifyCallback;
    this.state = {
      status: PerformanceTrackerStatus.Loading,
    };
  }

  public reportLoading() {
    this.updateStatus(PerformanceTrackerStatus.Loading);
  }

  public reportLoaded() {
    this.updateStatus(PerformanceTrackerStatus.Loaded);
  }

  public reportError({
    error,
    meta = null,
  }: {
    error?: Error;
    meta?: Record<string, string | number | boolean> | null;
  }) {
    this.updateStatus(PerformanceTrackerStatus.Error, error, meta);
  }

  public addTag(tag: string) {
    if (this.tags.includes(tag)) {
      return;
    }

    this.tags = [...this.tags, tag];
  }

  public removeTag(tag: string) {
    this.tags = this.tags.filter((t) => t !== tag);
  }

  public dispose() {
    if (this.state.status === PerformanceTrackerStatus.Loading) {
      this.updateStatus(PerformanceTrackerStatus.Canceled);
    }
  }

  private updateStatus(
    status: PerformanceTrackerStatus,
    error?: Error,
    meta: Record<string, string | number | boolean> | null = null
  ) {
    if (this.state.status === status) {
      return;
    }

    if (status === PerformanceTrackerStatus.Error && this.state.status !== PerformanceTrackerStatus.Error) {
      this.notifyCallback(`Error -- ${this.key}\n${this.stringifyTags()}`, {
        key: this.key,
        status,
        error,
        tags: this.tags,
        ...meta,
      });
    }

    this.state = {
      ...this.state,
      status,
      error,
    };
  }

  private stringifyTags() {
    return this.tags.map((tag) => `#${tag}`).join(' ');
  }
}
